'use client'
import React, { ReactElement, ReactNode, useEffect } from 'react'
import { Splide as SplideCore } from '@splidejs/splide'
import { ModalNew } from '@/components/common/ModalNew'
import classNames from 'classnames'
import { ResponsiveOptions } from '@splidejs/splide'
import { twMerge } from 'tailwind-merge'
import { useOnMobileOrTablet } from '@/lib/hooks'
import { Splide, SplideSlide } from '@splidejs/react-splide'

export type Breakpoints = Record<string | number, ResponsiveOptions>

interface HotelSliderProps {
  slidePadding?: number
  breakpoints?: Breakpoints
  sliderElements: ReactNode[]
  modalElements?: ReactNode[]
  selectedIndex?: number
  selectedModalIndex?: number
  setSelectedModalIndex?: (index: number | undefined) => void
  usedWithoutMargin?: boolean
  perPage?: number
  slideProps?: {
    className?: string
  }
  fixedWidth?: number
  focus?: number | 'center'
  test?: string
}

const DEFAULT_BREAKPOINTS = {
  320: {
    perPage: 1.3,
  },
  460: {
    perPage: 1.3,
  },
  620: {
    perPage: 1.75,
  },
  710: {
    perPage: 2.1,
  },
  840: {
    perPage: 2.5,
  },
  1024: { perPage: 2.1 },
  1200: { perPage: 2.5 },
}

export function HotelSlider(props: HotelSliderProps): ReactElement | null {
  const currentSelectedIndexRef = React.useRef<number | undefined>()
  const [splide, setSplide] = React.useState<Splide>()

  const isMobileOrTablet = useOnMobileOrTablet()

  useEffect(() => {
    if (!splide) {
      return
    }
    splide.go(props.selectedIndex || 0)
    currentSelectedIndexRef.current = props.selectedIndex || 0
    if (props.selectedIndex !== undefined) {
      if (props.selectedIndex !== currentSelectedIndexRef.current) {
        splide.go(props.selectedIndex)
      }
      currentSelectedIndexRef.current = props.selectedIndex
    }

    if (props.focus === undefined) {
      if (!splide.splide?.root.classList.contains('is-overflow') || props.focus) {
        setFocus(splide.splide, 0)
      }

      splide.splide?.on('resize', () => {
        if (splide) {
          if (!splide.splide?.root.classList.contains('is-overflow')) {
            setFocus(splide.splide, 0)
          } else {
            setFocus(splide.splide, 'center')
          }
        }
      })
    } else {
      setFocus(splide.splide, props.focus)
    }
  }, [splide, props.selectedIndex])

  useEffect(() => {
    if (!splide) {
      return
    }

    splide.splide?.on('moved', (newIndex) => {
      currentSelectedIndexRef.current = newIndex
    })
    const nextArrow = splide.splideRef.current?.querySelector('.splide__arrow--next')

    function clickListener() {
      if (currentSelectedIndexRef.current === 0) {
        // Skip to the 2nd slide
        splide?.go(2)
      } else {
        // Default single move forward
        splide?.go(currentSelectedIndexRef.current! + 1)
      }
    }

    nextArrow?.addEventListener('click', clickListener)
    return () => nextArrow?.removeEventListener('click', clickListener)
  }, [splide])

  function setFocus(splide: SplideCore | undefined, focus: number | 'center') {
    if (!splide) {
      return
    }

    splide.options = { ...splide.options, focus: focus }
  }

  function updateSplide(splide: Splide) {
    setSplide(splide)
  }

  function closeModal() {
    props.setSelectedModalIndex?.(undefined)
  }

  return (
    <div
      className={classNames('-mx-5', { 'lg:mx-0': !(props.usedWithoutMargin && isMobileOrTablet) })}
    >
      <Splide
        key={isMobileOrTablet.toString()}
        options={{
          perPage: !props.fixedWidth ? props.perPage || 1.2 : undefined,
          focus: 1,
          perMove: 1,
          flickPower: 200,
          speed: 600,
          pagination: false,
          gap: props.usedWithoutMargin && isMobileOrTablet ? undefined : '2rem',
          fixedWidth: props.fixedWidth,
          omitEnd: true,
          classes: {
            prev: 'splide__arrow--prev bg-white shadow-md',
            next: 'splide__arrow--next bg-white shadow-md',
          },
          mediaQuery: 'min',
          arrowPath:
            'M31.2538 19.9967L30.1252 21.1252L17.3857 33.8714L16.2571 35L14.0066 32.7429L15.1352 31.6143L26.7528 19.9967L15.1286 8.37907L14 7.2505L16.2571 5L17.3857 6.12857L30.1319 18.8681L31.2605 19.9967H31.2538Z',
          breakpoints: !props.fixedWidth ? props.breakpoints || DEFAULT_BREAKPOINTS : undefined,
        }}
        ref={updateSplide}
      >
        {props.sliderElements.map((sliderElement, index) => (
          <SplideSlide
            key={index}
            className={twMerge(
              classNames('py-4', {
                [`pr-${props.slidePadding ?? 4} pl-[18px]`]:
                  index === 0 && props.usedWithoutMargin && isMobileOrTablet,
                [`px-${props.slidePadding ?? 4}`]:
                  index !== 0 &&
                  index !== props.sliderElements.length - 1 &&
                  props.usedWithoutMargin &&
                  isMobileOrTablet,
                [`pl-${props.slidePadding ?? 4} pr-[18px]`]:
                  index === props.sliderElements.length - 1 &&
                  props.usedWithoutMargin &&
                  isMobileOrTablet,
              }),
              props.slideProps?.className,
            )}
          >
            {sliderElement}
          </SplideSlide>
        ))}
      </Splide>

      {props.modalElements?.map((modalElement, index) => (
        <ModalNew
          key={index}
          close={closeModal}
          // close={() => false}
          isOpen={props.selectedModalIndex == index}
          title={''}
          className="max-w-[640px] sm:max-w-[640px]"
        >
          {modalElement}
        </ModalNew>
      ))}
    </div>
  )
}
